/**
 * BasicsSection — workshop detail "Basics" (BON-117): Workshop type, Capacity,
 * Price. PRESENTATIONAL only — the page owns the consolidated edit mode
 * (useWorkshopEditForm), so this renders either the read grid or the form
 * inputs based on `editing`; there is no per-section Edit/Save here.
 *
 * The "Workshop type" field is a typeahead combobox over the ops /workshop-types
 * list. Selecting a type surfaces its species. Capacity +
 * Price are always editable; Price holds a bare number with a static "$" prefix.
 */
import type { WorkshopDetail } from '../../../../api/workshops';
import { PriceInput } from '../../primitives/PriceInput';
import { ReadGrid } from '../../venues/detail/ReadGrid';
import { Input } from '../../venues/detail/sectionHelpers';
import { Field } from '../../venues/venueCreateFields';
import { useWorkshopTypeOptions } from '../useWorkshopTypeOptions';

import type { WorkshopEditDraft, WorkshopFieldErrors } from './useWorkshopEditForm';
import { formatPrice, priceInputValue } from './workshopFields';
import { WorkshopTypeCombobox } from './WorkshopTypeCombobox';

interface Props {
  workshop: WorkshopDetail;
  editing: boolean;
  draft: WorkshopEditDraft;
  errors: WorkshopFieldErrors;
  setField: (key: keyof WorkshopEditDraft, value: string) => void;
}

export function BasicsSection({ workshop, editing, draft, errors, setField }: Props) {
  const { options, byId, defaultsById } = useWorkshopTypeOptions(workshop.workshopType);

  // Species follows the (possibly just-picked) type; falls back to the loaded
  // workshop's own type while options resolve.
  const selectedType = byId.get(draft.workshopTypeId) ?? workshop.workshopType;
  const speciesName = selectedType.treeSpecies?.name ?? null;

  // On a type CHANGE only (not load/save/reactive): seed capacity + price from
  // the new type's defaults. A null default leaves that field untouched, and a
  // later manual edit persists (this fires only on the combobox change event).
  const onTypeChange = (id: string) => {
    setField('workshopTypeId', id);
    const defaults = defaultsById.get(id);
    if (!defaults) return;
    if (defaults.defaultCapacity !== null) setField('capacity', String(defaults.defaultCapacity));
    if (defaults.defaultPriceCents !== null) {
      setField('price', priceInputValue(defaults.defaultPriceCents));
    }
  };

  if (!editing) {
    return (
      <ReadGrid
        cols={3}
        rows={[
          {
            label: 'Workshop type',
            value: workshop.workshopType.displayName ?? workshop.workshopType.name,
          },
          { label: 'Tree species', value: workshop.workshopType.treeSpecies?.name ?? null },
          { label: 'Capacity', value: workshop.capacity },
          { label: 'Price', value: formatPrice(workshop.priceCents) },
        ]}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
      <Field label="Workshop type" required error={errors.workshopTypeId}>
        <WorkshopTypeCombobox
          value={draft.workshopTypeId}
          options={options}
          onChange={onTypeChange}
          error={!!errors.workshopTypeId}
        />
      </Field>
      <Field label="Capacity" required error={errors.capacity}>
        <Input
          value={draft.capacity}
          onChange={(v) => setField('capacity', v)}
          error={!!errors.capacity}
        />
      </Field>
      <Field label="Price" required error={errors.price}>
        <PriceInput
          value={draft.price}
          error={errors.price}
          onChange={(v) => setField('price', v)}
        />
      </Field>
      {speciesName && (
        <div style={{ gridColumn: '1 / -1', fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          Species: {speciesName}
        </div>
      )}
    </div>
  );
}
